import { apiCall } from './api-call-helper';
import { getClientType } from '@common/services/detect-context';


export async function createMessageAttemptLog(
  messageId: string, 
  attemptType: 'initial' | 'client-retry' | 'scheduler-retry',
  whatsappStatus: string,
  logMessage: string,
  retryTrigger?: 'manual' | 'mqtt' | 'fcm',
  mqttClientId?: string,
  clientType?: string,
  waVersion?: string,
  waMeUser?: string,
  debugInfo?: { totalMessagesFetched?: number; top5Messages?: Array<{ id: string; body?: string; caption?: string }> },
  error?: any,
  waState?: string | null,
  waStream?: string | null,
  waHasSynced?: boolean | null
): Promise<{ success: boolean; error?: string }> {
  const data = {
    messageId,
    attemptType,
    whatsappStatus,
    logMessage,
    retryTrigger,
    mqttClientId,
    clientType,
    waVersion,
    waMeUser,
    debugInfo,
    error,
    waState,
    waStream,
    waHasSynced,
  };

  return apiCall<any>('post', '/actions?cmd=create-message-attempt-log', data);
}

export async function deleteUserMessageAndCreateLog(
  messageId: string, 
  waMessageKey: string, 
  sendFailureError?: any,
  attemptType?: 'initial' | 'client-retry' | 'scheduler-retry',
  whatsappStatus?: string,
  logMessage?: string,
  retryTrigger?: 'manual' | 'mqtt' | 'fcm',
  mqttClientId?: string,
  clientType?: string,
  waVersion?: string,
  waMeUser?: string,
  debugInfo?: { totalMessagesFetched?: number; top5Messages?: Array<{ id: string; body?: string; caption?: string }> },
  waState?: string | null,
  waStream?: string | null,
  waHasSynced?: boolean | null
): Promise<{
  isLogCreated: boolean,
  isMessageDeleted: boolean,
  isRecurringScheduled: boolean,
}> {
  const data = {
    messageId,
    waMessageKey,
    sendFailureError,
    attemptType,
    whatsappStatus,
    logMessage,
    retryTrigger,
    mqttClientId,
    clientType,
    waVersion,
    waMeUser,
    debugInfo,
    waState,
    waStream,
    waHasSynced,
  };

  return apiCall<any>('post', '/actions?cmd=delete-user-message-and-create-log', data);
}

/**
 * Promote a sent_pending_ack message to confirmed once this engine's extended ack-watch sees the
 * ack reach SERVER (>=1) past the bounded in-send follow window. The backend confirms + dequeues
 * (idempotent no-op if already confirmed). Reliable promotion trigger — the sending engine owns the
 * message's ack, so we never depend on the sparse/disabled ack-update flow.
 */
export async function promotePendingAck(
  messageId: string,
  ack: number,
  clientType?: string,
): Promise<{ success?: boolean; error?: string }> {
  return apiCall<any>('post', '/actions?cmd=promote-pending-ack', { messageId, ack, clientType });
}

export async function hasSendAttemptBeenMadeToMessage(messageId: string, retryAttemptNumber: number = 0, isSendMessageNow: boolean = false): Promise<any> {
  const data = {
    messageId,
    retryAttemptNumber,
    isSendMessageNow,
  };

  return apiCall<any>('post', '/actions?cmd=has-send-attempt-been-made-to-message', data);
}

export async function checkInitialAttemptLockExists(messageId: string): Promise<{ initialAttemptLockExists: boolean; messageId: string }> {
  const data = {
    messageId,
  };

  return apiCall<{ initialAttemptLockExists: boolean; messageId: string }>('post', '/actions?cmd=check-initial-attempt-lock-exists', data);
}

export async function removeSendAttemptLockFromRedis(messageId: string): Promise<any> {
  const data = {
    messageId,
  };

  return apiCall<any>('post', '/actions?cmd=remove-send-attempt-lock-from-redis', data);
}
export async function getNextCampaignMessage(campaignId: string): Promise<any> {
  const data = {
    campaignId,
  };

  return apiCall<any>('post', '/actions?cmd=next-campaign-message', data);
}

export async function updateUserMessageAck(messageId: string, ack: number): Promise<any> {
  const data = {
    messageId,
    ack,
  };

  return apiCall<any>('post', '/actions?cmd=update-user-message-ack', data);
}

export async function getSettingsRequest(): Promise<any> {

  return apiCall<any>('post', '/actions?cmd=get-settings');
}

export async function createMessageAckUpdateLog(
  messageId: string,
  ackData: {
    deliveryRemaining?: number;
    readRemaining?: number;
    playedRemaining?: number;
    totalRecipients?: number;
  },
  existingLog: any,
  mqttClientId?: string,
  clientType?: string
): Promise<{ success: boolean; error?: string }> {
  const data = {
    messageId,
    ackData,
    existingLog,
    mqttClientId,
    clientType,
  };

  return apiCall<any>('post', '/actions?cmd=create-message-ack-update-log', data);
}

/**
 * Durable, no-flood delivery-ack recorder. Called ONLY for Blueticks-sent
 * messages (self-identified via the embedded messageSecret → userMessageId), so
 * the messagelogs collection is never flooded with the user's unrelated
 * WhatsApp traffic. The backend stamps the ack onto the message's existing
 * confirmed send-log in place (monotonic), keyed by messageId — no dependency
 * on the log being present in the extension's transient Redux.
 */
export async function recordMessageDeliveryAck(
  messageId: string, // userMessageId (== messagelogs.messageId)
  ack: number,
  ackData: {
    deliveryRemaining?: number;
    readRemaining?: number;
    playedRemaining?: number;
    totalRecipients?: number;
  },
  waMessageKey?: string,
): Promise<{ success: boolean; error?: string }> {
  const data = { messageId, ack, ackData, waMessageKey };
  return apiCall<any>('post', '/actions?cmd=record-message-delivery-ack', data);
}

export async function createBatchMessageAckUpdateLogs(
  logs: Array<{
    messageId: string;
    ackData: {
      deliveryRemaining?: number;
      readRemaining?: number;
      playedRemaining?: number;
      totalRecipients?: number;
    };
    existingLog: any;
  }>
): Promise<{ success: boolean; created?: number; error?: string }> {
  const data = {
    logs,
  };

  return apiCall<any>('post', '/actions?cmd=create-batch-message-ack-update-logs', data);
}

export async function createFailedMessageLog(
  messageId: string,
  failureReason: string,
  attemptType: 'initial' | 'client-retry' | 'scheduler-retry',
  whatsappStatus: string,
  retryTrigger?: 'manual' | 'mqtt' | 'fcm',
  mqttClientId?: string,
  clientType?: string,
  waVersion?: string,
  waMeUser?: string,
  debugInfo?: any,
  error?: any,
  waState?: string | null,
  waStream?: string | null,
  waHasSynced?: boolean | null
): Promise<{ success: boolean; error?: string }> {
  const data = {
    messageId,
    failureReason,
    attemptType,
    whatsappStatus,
    retryTrigger,
    mqttClientId,
    clientType,
    waVersion,
    waMeUser,
    debugInfo,
    error,
    waState,
    waStream,
    waHasSynced,
  };

  return apiCall<any>('post', '/actions?cmd=create-failed-message-log', data);
}

export interface InitErrorReportData {
  errors: Array<{
    step: string;
    stepNumber: string;
    errorMessage: string;
    timestamp: string;
    stack?: string;
    context?: Record<string, any>;
  }>;
  steps: Array<{
    step: string;
    stepNumber: string;
    status: string;
    duration?: number;
  }>;
  userAgent: string;
  url: string;
  timestamp: string;
  userId?: string;
  userEmail?: string;
  /** BE#813: stamped by sendInitErrorReport so the backend can label the counter by version. */
  extensionVersion?: string;
  /** BE#813: 'gateway' | 'whatsapp' | 'frontend' — stamped by sendInitErrorReport. */
  clientType?: string;
}

export async function sendInitErrorReport(data: InitErrorReportData): Promise<{ success: boolean; error?: string }> {
  console.log('[INIT API] Sending error report to backend...', data);
  // BE#813: the backend tracks init errors as a Loki line + counter labelled by extension version and
  // client type (it no longer emails them). Guarded, no-throw — stamping must never block the report.
  const stamped: InitErrorReportData = { ...data };
  try {
    const v = (globalThis as any).chrome?.runtime?.getManifest?.()?.version;
    if (v && !stamped.extensionVersion) stamped.extensionVersion = v;
  } catch { /* not in an extension context */ }
  try {
    if (!stamped.clientType) stamped.clientType = getClientType();
  } catch { /* detection unavailable */ }
  return apiCall<any>('post', '/actions?cmd=send-init-error-report', stamped);
}

/** #44 telemetry: count-only "incoming message not rendered" report. The payload is whitelisted
 * here (no ids, no content) and stamped with the extension version for the backend counter label. */
export async function reportIncomingNotRendered(data: {
  storeHasMsg?: boolean;
  filterEver?: boolean;
  chatMsgsLength?: number;
  chatModelsLength?: number;
  chatIndexSize?: number;
  injectionFlags?: string[];
}): Promise<{ success: boolean; error?: string }> {
  const body: Record<string, unknown> = {
    storeHasMsg: data?.storeHasMsg,
    filterEver: data?.filterEver,
    chatMsgsLength: data?.chatMsgsLength,
    chatModelsLength: data?.chatModelsLength,
    chatIndexSize: data?.chatIndexSize,
    injectionFlags: data?.injectionFlags,
  };
  try {
    const v = (globalThis as any).chrome?.runtime?.getManifest?.()?.version;
    if (v) body.extensionVersion = v;
  } catch { /* not in an extension context */ }
  return apiCall<any>('post', '/actions?cmd=report-incoming-not-rendered', body);
}

export async function recordPayFunnelEvent(
  name: string,
  props?: Record<string, unknown>,
): Promise<{ success: boolean; error?: string; exposures?: number | null; offerDeclined?: boolean }> {
  const body: Record<string, unknown> = props ? { name, props } : { name };
  return apiCall<any>('post', '/actions?cmd=record-pay-funnel-event', body);
}

/**
 * Durable agent/bot product-event ingest. Mirrors `recordPayFunnelEvent`: it POSTs
 * to the backend `events` bus via the same `apiCall` mechanism, so it works in BOTH
 * the web app AND the extension/content-script context — unlike `AnalyticsService.event()`,
 * which routes to `gtag` and silently drops everything in extension context (where gtag
 * is unavailable). All `agent_*` / `bot_*` / `kb_*` product events route through here so
 * they reach a durable backend sink instead of the floor.
 *
 * Callers should go through `reportAgentEvent()` (agent-analytics.ts), which stamps the
 * mandatory `platform` prop and drops any `chatId`/`chatIdHash` (chatId is server-only
 * now — the backend does its own HMAC). Never put message bodies in `props`.
 */
export async function recordAgentEvent(
  name: string,
  props?: Record<string, unknown>,
): Promise<{ success: boolean; error?: string }> {
  const body: Record<string, unknown> = props ? { name, props } : { name };
  return apiCall<any>('post', '/actions?cmd=record-agent-event', body);
}

/** #113 FE-1 — tell the backend the extension engine is the connected engine
 * (the extension IS the engine, so there's no separate "connect" step to
 * click through — the server records `whatsapp_connected{engine:'extension'}`
 * on receipt). See reportEngineConnectedOnceThunk for the once-per-load gate. */
export async function reportEngineConnected(): Promise<{ success: boolean; emitted?: boolean }> {
  return apiCall<any>('post', '/actions?cmd=report-engine-connected', {});
}

/** Mint a Chargebee hosted-checkout URL for a specific item price (plan+cycle).
 * Server action: chargebee.getCheckoutPage (actions endpoint). */
export async function getCheckoutPageUrl(
  item_price_id: string,
): Promise<{ checkoutPageUrl?: string; error?: string }> {
  return apiCall<any>('post', '/actions', {
    cmd: 'chargebee.getCheckoutPage',
    item_price_id,
    quantity: 1,
  });
}

/** Start a no-card 7-day Chargebee Pro trial for the workspace (Option-3 fatigue-cap
 * offer). Server action: `start-pro-trial` (actions endpoint). `ineligible: true` means
 * the workspace already had a subscription at some point — callers should fall back to
 * the normal Pro checkout in that case. */
export async function startProTrial(): Promise<{ ok?: boolean; ineligible?: boolean; trialEnd?: string; subscriptionId?: string; reason?: string }> {
  return apiCall<any>('post', '/actions', { cmd: 'start-pro-trial' });
}

/** #1226 slice A: in-trial upgrade popup. `get` returns the server decision (show, copy, arm);
 * shown / clicked / dismissed record the outcome. Server-side flag; inert while it is off. */
export type TrialUpgradePromptType = 'gateway' | 'mcp' | 'sender';
export type TrialUpgradePromptOp = 'get' | 'shown' | 'clicked' | 'dismissed';
export interface TrialUpgradePromptCopy { heading: string; body: string; button: string; notNow: string; footer: string }
export interface TrialUpgradePromptResponse {
  show: boolean; type: TrialUpgradePromptType | null; n: number; plan: 'pro' | 'basic' | null;
  price: number | null; trialEnd: string | null; arm: 'holdout' | 'shown' | null; copy: TrialUpgradePromptCopy | null;
  /** #1229: set only when the server verified the no-charge card flow is available. The popup copy and the CTA both come from it. */
  cardQuote?: TrialCardQuote | null;
}
export async function getTrialUpgradePrompt(): Promise<TrialUpgradePromptResponse> {
  return apiCall<TrialUpgradePromptResponse>('post', '/actions', { cmd: 'trial-upgrade-prompt', op: 'get', cardQuoteCapable: true });
}
export async function recordTrialUpgradePromptOp(op: Exclude<TrialUpgradePromptOp, 'get'>, type: TrialUpgradePromptType | null): Promise<{ recorded: boolean }> {
  return apiCall<{ recorded: boolean }>('post', '/actions', { cmd: 'trial-upgrade-prompt', op, type });
}

/** #1229: customer is back from the Chargebee hosted page; reconcile the pending trial-card consent.
 * Server-side flag and allowlist; answers `disabled` while off. */
export interface TrialCardAddedConfirmation { title: string; body: string }
export type ReconcileTrialCardCaptureOutcome =
  | { kind: 'activated' | 'already_activated'; confirmation: TrialCardAddedConfirmation }
  | { kind: string; confirmation?: undefined };
export async function reconcileTrialCardCapture(): Promise<ReconcileTrialCardCaptureOutcome> {
  return apiCall<ReconcileTrialCardCaptureOutcome>('post', '/actions', { cmd: 'chargebee.reconcileTrialCardCapture' });
}

/** #1229: consent screen. `getTrialCardQuote` is read-only and returns the server-built consent copy;
 * `startTrialCardCapture` records the consent and returns the Chargebee hosted page URL. Both are
 * flag + allowlist + arm gated server-side; any `kind` other than 'ok' means "use the normal checkout". */
export interface TrialCardConsentCopy {
  textVersionId: string; title: string; body: string; cancelLine: string; refundLine: string; emailNotice: string;
  checkbox: string; button: string; partnerNotice: string; euWithdrawal: string | null;
}
export interface TrialCardQuote {
  plan: string; itemPriceId: string; monthlyMinor: number; currency: string; chargeDateEpoch: number; copy: TrialCardConsentCopy;
}
export type TrialCardQuoteResponse = ({ kind: 'ok' } & TrialCardQuote) | { kind: Exclude<string, 'ok'> };
/** `plan` is the popup's plan: the one server decision both screens read. */
export async function getTrialCardQuote(plan: 'pro' | 'basic'): Promise<TrialCardQuoteResponse> {
  return apiCall<TrialCardQuoteResponse>('post', '/actions', { cmd: 'chargebee.getTrialCardQuote', plan });
}
/** The price, plan and charge date the customer was shown; the server refuses with quote_changed if Chargebee moved. */
export interface TrialCardShownQuote { itemPriceId: string; monthlyMinor: number; currency: string; chargeDateEpoch: number }
export type StartTrialCardCaptureResponse =
  | { kind: 'ok'; url: string; consentId: string }
  | ({ kind: 'quote_changed' } & TrialCardQuote)
  | { kind: Exclude<string, 'ok' | 'quote_changed'> };
export async function startTrialCardCapture(params: { checkboxAccepted: true; locale: string; country: string; redirectUrl: string; shown: TrialCardShownQuote }): Promise<StartTrialCardCaptureResponse> {
  return apiCall<StartTrialCardCaptureResponse>('post', '/actions', { cmd: 'chargebee.startTrialCardCapture', ...params });
}

/** #691: would `start-pro-trial` grant this user a trial? Read-only; `eligible: false` when the
 * workspace or the user's email already had a subscription (or Chargebee could not be checked). */
export async function getProTrialEligibility(): Promise<{ eligible: boolean; reason?: string }> {
  return apiCall<{ eligible: boolean; reason?: string }>('post', '/actions', { cmd: 'pro-trial-eligibility' });
}
